
import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

interface ProgressData {
  year: string;
  target: number;
  realisasi: number;
  percentage: number;
  activities: string[];
  challenges: string[];
  achievements: string[];
}

export default function SekolahLapangPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'progress' | 'impact' | 'methodology'>('overview');

  const progressData: ProgressData[] = [
    {
      year: '2021',
      target: 15,
      realisasi: 12,
      percentage: 80,
      activities: ['Basic aquaculture training in 12 villages', 'Mangrove cultivation workshops', 'Community leader capacity building'],
      challenges: ['Limited technical expertise in communities', 'Seasonal weather disruptions', 'Initial resistance to new methods'],
      achievements: ['12 successful field schools established', '336 participants trained', 'Formation of 8 farmer groups']
    },
    {
      year: '2022',
      target: 25,
      realisasi: 23,
      percentage: 92,
      activities: ['Advanced aquaculture techniques training', 'Business development workshops', 'Peer-to-peer learning sessions'],
      challenges: ['Market access limitations', 'Equipment procurement delays', 'Maintaining engagement momentum'],
      achievements: ['23 field schools operational', '644 participants certified', 'Established 15 cooperative networks']
    },
    {
      year: '2023',
      target: 35,
      realisasi: 31,
      percentage: 89,
      activities: ['Integrated farming system training', 'Value chain development programs', 'Technology adoption workshops'],
      challenges: ['Climate variability impacts', 'Scaling quality with quantity', 'Resource allocation optimization'],
      achievements: ['31 advanced programs delivered', '868 participants graduated', '23 successful enterprises launched']
    },
    {
      year: '2024',
      target: 45,
      realisasi: 42,
      percentage: 93,
      activities: ['Innovation and technology integration', 'Market linkage facilitation', 'Sustainability planning workshops'],
      challenges: ['Ensuring long-term sustainability', 'Technology adoption barriers', 'Measuring behavioral change'],
      achievements: ['42 comprehensive programs completed', '1,176 participants certified', '35 community-led initiatives active']
    }
  ];

  const impactMetrics = [
    { label: 'Total Participants Trained', value: '3,024', unit: 'People', icon: 'ri-group-line', color: '#8B4513' },
    { label: 'Field Schools Established', value: '108', unit: 'Schools', icon: 'ri-school-line', color: '#20B2AA' },
    { label: 'Skill Certification Rate', value: '89%', unit: 'Success Rate', icon: 'ri-award-line', color: '#2D8B57' },
    { label: 'Income Increase', value: '156%', unit: 'Average', icon: 'ri-line-chart-line', color: '#FF6B35' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-[#8B4513] to-[#A0522D] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-school-line text-3xl"></i>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                Sekolah Lapang Program
              </h1>
              <p className="text-xl sm:text-2xl mb-8 opacity-90">
                Practical field training for sustainable aquaculture and mangrove conservation techniques
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#8B4513] whitespace-nowrap"
                >
                  <i className="ri-download-line mr-2"></i>
                  Training Materials
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#8B4513] whitespace-nowrap"
                >
                  <i className="ri-calendar-line mr-2"></i>
                  Training Schedule
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Navigation Tabs */}
        <section className="py-8 bg-white border-b">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-wrap gap-2 justify-center">
                {[
                  { id: 'overview', label: 'Program Overview', icon: 'ri-eye-line' },
                  { id: 'progress', label: 'Progress & Targets', icon: 'ri-bar-chart-line' },
                  { id: 'impact', label: 'Impact Assessment', icon: 'ri-award-line' },
                  { id: 'methodology', label: 'Methodology', icon: 'ri-file-text-line' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`px-6 py-3 rounded-full font-medium transition-all duration-200 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-[#8B4513] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className={`${tab.icon} mr-2`}></i>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Sekolah Lapang Program Overview
                  </h2>
                  <p className="text-lg text-gray-600">
                    Hands-on field training program transforming coastal communities through practical education
                  </p>
                </div>

                {/* Storytelling Section */}
                <div className="mb-16">
                  <div className="bg-gradient-to-r from-[#8B4513]/10 to-[#A0522D]/10 rounded-xl p-8 mb-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-[#8B4513]/10 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-book-open-line text-2xl text-[#8B4513]"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-[#333333]">From Fisherman to Aquaculture Expert</h3>
                    </div>
                    
                    <div className="prose prose-lg max-w-none">
                      <p className="text-gray-700 leading-relaxed mb-6">
                        "I never thought I could learn something new at 45," says Pak Budi, a traditional fisherman from Teluk Meranti. Three years ago, he struggled with declining fish catches and unpredictable income. Today, he manages three successful fish ponds and teaches modern aquaculture techniques to other fishermen in his village.
                      </p>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        <div>
                          <img
                            src="https://public.readdy.ai/ai/img_res/21a2c171f3f79c915d7d613ef505f929.jpg"
                            alt="Field School Training Session"
                            className="w-full h-48 object-cover rounded-lg mb-4"
                          />
                          <p className="text-sm text-gray-600 italic">
                            Pak Budi learning pond management techniques at Teluk Meranti Field School, 2022
                          </p>
                        </div>
                        
                        <div className="flex flex-col justify-center">
                          <blockquote className="border-l-4 border-[#8B4513] pl-6 mb-6">
                            <p className="text-lg text-gray-700 italic mb-4">
                              "The Field School didn't just teach me techniques—it gave me confidence to try new things and share knowledge with my community."
                            </p>
                            <footer className="text-[#8B4513] font-bold">— Pak Budi Santoso, Aquaculture Entrepreneur</footer>
                          </blockquote>
                          
                          <p className="text-gray-700 leading-relaxed">
                            Pak Budi's transformation represents the core philosophy of Sekolah Lapang: when communities master practical skills through hands-on learning, they become agents of their own economic development.
                          </p>
                        </div>
                      </div>
                      
                      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                        <h5 className="font-bold text-[#333333] mb-3">The Learning Revolution: Practice Makes Perfect</h5>
                        <p className="text-gray-700 text-sm leading-relaxed mb-4">
                          Unlike traditional classroom training, Sekolah Lapang uses real ponds, actual mangrove sites, and working aquaculture systems as learning laboratories. This hands-on approach achieves 89% skill retention rates compared to 40% for lecture-based programs.
                        </p>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <div className="text-2xl font-bold text-[#8B4513] mb-1">3,024</div>
                            <div className="text-xs text-gray-600">People trained</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-[#20B2AA] mb-1">108</div>
                            <div className="text-xs text-gray-600">Field schools</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-[#2D8B57] mb-1">89%</div>
                            <div className="text-xs text-gray-600">Skill retention</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Journalist Report Section */}
                  <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-newspaper-line text-2xl text-[#20B2AA]"></i>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-[#333333]">Field Report: Education That Works</h3>
                        <p className="text-gray-600">How Field Schools achieve 93% success rates in community transformation</p>
                      </div>
                    </div>
                    
                    <div className="prose prose-lg max-w-none">
                      <div className="bg-[#F8F9FA] border-l-4 border-[#20B2AA] p-6 mb-6 rounded-r-lg">
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Field Investigation</strong> • December 2024 • M4CR Research Team
                        </p>
                        <h4 className="text-lg font-bold text-[#333333] mb-3">
                          The Science Behind 89% Skill Retention: What Makes Field Schools Different
                        </h4>
                      </div>
                      
                      <p className="text-gray-700 leading-relaxed mb-6">
                        <strong>DEMAK, Central Java</strong> — At sunrise, 25 fishermen gather around a demonstration pond where Bu Sari explains the optimal feeding schedule for milkfish. Unlike traditional training programs, there are no PowerPoint slides or classroom lectures. Instead, participants roll up their sleeves and practice pond management techniques in real aquaculture systems.
                      </p>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                        <h5 className="font-bold text-blue-800 mb-4">The Three-Phase Learning Model</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div>
                            <h6 className="font-semibold text-blue-700 mb-3">Foundation (Weeks 1-2)</h6>
                            <ul className="space-y-2 text-sm text-blue-700">
                              <li>• Basic ecosystem understanding</li>
                              <li>• Safety protocols and best practices</li>
                              <li>• Traditional knowledge integration</li>
                              <li>• Community group formation</li>
                            </ul>
                          </div>
                          <div>
                            <h6 className="font-semibold text-blue-700 mb-3">Application (Weeks 3-6)</h6>
                            <ul className="space-y-2 text-sm text-blue-700">
                              <li>• Hands-on technique practice</li>
                              <li>• Equipment operation training</li>
                              <li>• Problem-solving exercises</li>
                              <li>• Peer learning sessions</li>
                            </ul>
                          </div>
                          <div>
                            <h6 className="font-semibold text-blue-700 mb-3">Mastery (Weeks 7-8)</h6>
                            <ul className="space-y-2 text-sm text-blue-700">
                              <li>• Independent project implementation</li>
                              <li>• Teaching other community members</li>
                              <li>• Business planning and development</li>
                              <li>• Certification assessment</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <blockquote className="border-l-4 border-[#8B4513] pl-6 mb-6 bg-[#F8F9FA] p-4 rounded-r-lg">
                        <p className="text-lg text-gray-700 italic mb-4">
                          "When people learn by doing, they don't just remember techniques—they understand the principles behind them. That's why our graduates become innovators, not just followers."
                        </p>
                        <footer className="text-[#8B4513] font-bold">— Dr. Sarah Wijaya, M4CR Education Director</footer>
                      </blockquote>
                    </div>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  {impactMetrics.map((metric, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${metric.color}20` }}
                      >
                        <i className={`${metric.icon} text-2xl`} style={{ color: metric.color }}></i>
                      </div>
                      <div className="text-3xl font-bold mb-2" style={{ color: metric.color }}>
                        {metric.value}
                      </div>
                      <div className="text-sm text-gray-500 mb-1">{metric.unit}</div>
                      <div className="text-[#333333] font-medium">{metric.label}</div>
                    </div>
                  ))}
                </div>

                {/* Program Description */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Program Objectives</h3>
                    <div className="space-y-4">
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#8B4513]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#8B4513]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Practical Skill Development</h4>
                          <p className="text-gray-600 text-sm">
                            Hands-on training in sustainable aquaculture, mangrove cultivation, and eco-tourism development
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#20B2AA]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#20B2AA]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Community Leadership</h4>
                          <p className="text-gray-600 text-sm">
                            Building local capacity for community-led conservation and development initiatives
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#2D8B57]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#2D8B57]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Economic Empowerment</h4>
                          <p className="text-gray-600 text-sm">
                            Creating sustainable livelihood opportunities through skill-based enterprises
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#FF6B35]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#FF6B35]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Knowledge Transfer</h4>
                          <p className="text-gray-600 text-sm">
                            Establishing peer-to-peer learning networks for sustainable knowledge sharing
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Training Modules</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Sustainable Aquaculture</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Pond management, fish farming techniques, and integrated aquaculture systems
                        </p>
                        <div className="text-xs text-[#8B4513] font-medium">67 training sessions conducted</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Mangrove Cultivation</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Seedling production, planting techniques, and ecosystem management
                        </p>
                        <div className="text-xs text-[#20B2AA] font-medium">45 practical workshops delivered</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Business Development</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Entrepreneurship skills, financial management, and market linkage development
                        </p>
                        <div className="text-xs text-[#2D8B57] font-medium">38 business training programs</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Eco-tourism Development</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Guide training, service development, and sustainable tourism practices
                        </p>
                        <div className="text-xs text-[#FF6B35] font-medium">23 eco-tourism programs launched</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Success Stories */}
                <div className="bg-gradient-to-r from-[#8B4513]/10 to-[#A0522D]/10 rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Success Stories</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-white rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <img
                          src="https://readdy.ai/api/search-image?query=Indonesian%20middle-aged%20fisherman%20portrait%2C%20confident%20smile%2C%20traditional%20fishing%20village%20background%2C%20professional%20headshot%20style%2C%20natural%20lighting%2C%20coastal%20setting&width=60&height=60&seq=testimonial-1&orientation=squarish"
                          alt="Pak Budi"
                          className="w-12 h-12 rounded-full object-cover mr-4"
                        />
                        <div>
                          <div className="font-bold text-[#333333]">Pak Budi Santoso</div>
                          <div className="text-sm text-gray-600">Aquaculture Entrepreneur, Teluk Meranti</div>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed italic">
                        "Field School taught me modern fish farming techniques. Now I manage three ponds and earn 200% more than fishing alone. I even teach other fishermen in my village."
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <img
                          src="https://readdy.ai/api/search-image?query=Indonesian%20woman%20entrepreneur%20portrait%2C%20confident%20expression%2C%20mangrove%20nursery%20background%2C%20professional%20headshot%20style%2C%20natural%20lighting%2C%20coastal%20business%20setting&width=60&height=60&seq=testimonial-2&orientation=squarish"
                          alt="Ibu Sari"
                          className="w-12 h-12 rounded-full object-cover mr-4"
                        />
                        <div>
                          <div className="font-bold text-[#333333]">Ibu Sari Dewi</div>
                          <div className="text-sm text-gray-600">Eco-tourism Guide, Demak</div>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed italic">
                        "I learned to guide tourists through mangrove forests. Now I run eco-tours and help preserve our environment while earning steady income year-round."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Progress Tracking & Target Achievement
                  </h2>
                  <p className="text-lg text-gray-600">
                    Four-year journey of scaling practical education programs
                  </p>
                </div>

                {/* Overall Progress Chart */}
                <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-12">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Annual Progress Overview</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    {progressData.map((data, index) => (
                      <div key={index} className="text-center">
                        <div className="text-3xl font-bold text-[#8B4513] mb-2">{data.year}</div>
                        <div className="text-lg font-medium text-[#333333] mb-2">{data.percentage}%</div>
                        <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                          <div 
                            className="bg-gradient-to-r from-[#8B4513] to-[#A0522D] h-3 rounded-full transition-all duration-1000"
                            style={{ width: `${data.percentage}%` }}
                          ></div>
                        </div>
                        <div className="text-sm text-gray-600">
                          {data.realisasi}/{data.target} schools
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#F8F9FA] rounded-lg p-6">
                    <h4 className="font-bold text-[#333333] mb-4 text-center">Cumulative Achievement</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#8B4513] mb-1">108</div>
                        <div className="text-sm text-gray-600">Total Field Schools</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#20B2AA] mb-1">88.5%</div>
                        <div className="text-sm text-gray-600">Overall Achievement</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#2D8B57] mb-1">3,024</div>
                        <div className="text-sm text-gray-600">People Trained</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detailed Annual Breakdown */}
                <div className="space-y-8">
                  {progressData.map((data, index) => (
                    <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-1">
                          <div className="flex items-center mb-4">
                            <div className="w-16 h-16 bg-gradient-to-r from-[#8B4513] to-[#A0522D] rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                              {data.year}
                            </div>
                            <div>
                              <div className="text-2xl font-bold text-[#333333]">{data.percentage}%</div>
                              <div className="text-gray-600">Achievement Rate</div>
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Target:</span>
                              <span className="font-bold text-[#333333]">{data.target} schools</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Realized:</span>
                              <span className="font-bold text-[#8B4513]">{data.realisasi} schools</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-[#8B4513] h-2 rounded-full"
                                style={{ width: `${data.percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                              <h4 className="font-bold text-[#333333] mb-3">Key Activities</h4>
                              <ul className="space-y-2">
                                {data.activities.map((activity, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <i className="ri-check-line text-[#8B4513] mr-2 mt-0.5 flex-shrink-0"></i>
                                    <span className="text-gray-700 text-sm">{activity}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div>
                              <h4 className="font-bold text-[#333333] mb-3">Challenges</h4>
                              <ul className="space-y-2">
                                {data.challenges.map((challenge, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <i className="ri-alert-line text-[#FF6B6B] mr-2 mt-0.5 flex-shrink-0"></i>
                                    <span className="text-gray-700 text-sm">{challenge}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div>
                              <h4 className="font-bold text-[#333333] mb-3">Achievements</h4>
                              <ul className="space-y-2">
                                {data.achievements.map((achievement, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <i className="ri-trophy-line text-[#FFD93D] mr-2 mt-0.5 flex-shrink-0"></i>
                                    <span className="text-gray-700 text-sm">{achievement}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Impact Tab */}
        {activeTab === 'impact' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Impact Assessment & Outcomes
                  </h2>
                  <p className="text-lg text-gray-600">
                    Measuring the transformative effects of practical field education
                  </p>
                </div>

                {/* Impact Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  {impactMetrics.map((metric, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center hover:shadow-lg transition-shadow duration-300">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${metric.color}20` }}
                      >
                        <i className={`${metric.icon} text-2xl`} style={{ color: metric.color }}></i>
                      </div>
                      <div className="text-3xl font-bold mb-2" style={{ color: metric.color }}>
                        {metric.value}
                      </div>
                      <div className="text-sm text-gray-500 mb-1">{metric.unit}</div>
                      <div className="text-[#333333] font-medium">{metric.label}</div>
                    </div>
                  ))}
                </div>

                {/* Skills Development Assessment */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Skills Development</h3>
                    <div className="space-y-6">
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Aquaculture Techniques</span>
                          <span className="font-bold text-[#8B4513]">94%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#8B4513] h-2 rounded-full" style={{ width: '94%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Mastery of sustainable fish farming techniques</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Mangrove Cultivation</span>
                          <span className="font-bold text-[#20B2AA]">87%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#20B2AA] h-2 rounded-full" style={{ width: '87%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Proficiency in mangrove planting and care</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Business Management</span>
                          <span className="font-bold text-[#2D8B57]">78%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#2D8B57] h-2 rounded-full" style={{ width: '78%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Basic entrepreneurship and financial management</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Eco-tourism Development</span>
                          <span className="font-bold text-[#FF6B35]">71%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#FF6B35] h-2 rounded-full" style={{ width: '71%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Tourism guide and service development skills</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Economic Outcomes</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#8B4513]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-line-chart-line text-[#8B4513]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">Income Increase</div>
                            <div className="text-2xl font-bold text-[#8B4513]">156%</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Average income improvement within 12 months of training completion
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-building-line text-[#20B2AA]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">New Ventures</div>
                            <div className="text-2xl font-bold text-[#20B2AA]">73%</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Participants who started new businesses or expanded existing ones
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-money-dollar-circle-line text-[#2D8B57]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">Income Sources</div>
                            <div className="text-2xl font-bold text-[#2D8B57]">2.3</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Average number of income sources per participant household
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Long-term Impact */}
                <div className="bg-gradient-to-r from-[#8B4513]/10 to-[#A0522D]/10 rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Long-term Impact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#8B4513]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-graduation-cap-line text-2xl text-[#8B4513]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Knowledge Transfer</h4>
                      <p className="text-gray-600 text-sm">
                        89% of graduates actively share knowledge with family and neighbors, creating multiplier effects
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-team-line text-2xl text-[#20B2AA]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Community Leadership</h4>
                      <p className="text-gray-600 text-sm">
                        45% of participants take on leadership roles in community conservation and development initiatives
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-leaf-line text-2xl text-[#2D8B57]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Environmental Stewardship</h4>
                      <p className="text-gray-600 text-sm">
                        91% of participants report increased environmental awareness and adopt sustainable practices
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Methodology Tab */}
        {activeTab === 'methodology' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Field School Methodology
                  </h2>
                  <p className="text-lg text-gray-600">
                    Adult learning principles and hands-on training approaches for maximum impact
                  </p>
                </div>

                {/* Learning Phases */}
                <div className="space-y-8 mb-12">
                  {[
                    {
                      phase: 1,
                      title: 'Foundation Phase (Weeks 1-2)',
                      description: 'Building basic understanding and establishing learning groups',
                      icon: 'ri-book-open-line',
                      color: '#8B4513',
                      activities: [
                        'Ecosystem understanding and traditional knowledge integration',
                        'Safety protocols and best practices introduction',
                        'Community group formation and goal setting',
                        'Baseline skill assessment and learning needs analysis'
                      ]
                    },
                    {
                      phase: 2,
                      title: 'Application Phase (Weeks 3-6)',
                      description: 'Hands-on practice and skill development in real environments',
                      icon: 'ri-tools-line',
                      color: '#20B2AA',
                      activities: [
                        'Practical technique demonstration and practice',
                        'Equipment operation and maintenance training',
                        'Problem-solving exercises and case studies',
                        'Peer learning sessions and knowledge sharing'
                      ]
                    },
                    {
                      phase: 3,
                      title: 'Mastery Phase (Weeks 7-8)',
                      description: 'Independent application and teaching others',
                      icon: 'ri-award-line',
                      color: '#2D8B57',
                      activities: [
                        'Independent project implementation and management',
                        'Teaching and mentoring other community members',
                        'Business planning and development exercises',
                        'Certification assessment and graduation ceremony'
                      ]
                    }
                  ].map((phase, index) => (
                    <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                      <div className="flex items-start space-x-6">
                        <div 
                          className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0"
                          style={{ backgroundColor: phase.color }}
                        >
                          {phase.phase}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center mb-4">
                            <i className={`${phase.icon} text-2xl mr-3`} style={{ color: phase.color }}></i>
                            <h3 className="text-2xl font-bold text-[#333333]">{phase.title}</h3>
                          </div>
                          <p className="text-gray-700 leading-relaxed text-lg mb-6">
                            {phase.description}
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {phase.activities.map((activity, idx) => (
                              <div key={idx} className="flex items-start space-x-3">
                                <div 
                                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                                  style={{ backgroundColor: `${phase.color}20` }}
                                >
                                  <i className="ri-check-line text-xs" style={{ color: phase.color }}></i>
                                </div>
                                <span className="text-gray-700 text-sm">{activity}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Learning Principles */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Adult Learning Principles</h3>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#8B4513]/10 rounded-full flex items-center justify-center">
                          <i className="ri-user-heart-line text-[#8B4513]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Experience-Based Learning</h4>
                          <p className="text-sm text-gray-600">Building on participants' existing knowledge and experience</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#20B2AA]/10 rounded-full flex items-center justify-center">
                          <i className="ri-lightbulb-line text-[#20B2AA]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Problem-Solving Approach</h4>
                          <p className="text-sm text-gray-600">Addressing real challenges faced by participants</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#2D8B57]/10 rounded-full flex items-center justify-center">
                          <i className="ri-team-line text-[#2D8B57]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Peer-to-Peer Learning</h4>
                          <p className="text-sm text-gray-600">Encouraging knowledge sharing among participants</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-full flex items-center justify-center">
                          <i className="ri-hand-heart-line text-[#FF6B35]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Immediate Application</h4>
                          <p className="text-sm text-gray-600">Applying new skills in real-world contexts</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Quality Assurance</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#8B4513]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-shield-check-line text-[#8B4513]"></i>
                          </div>
                          <h4 className="font-bold text-[#333333]">Certified Trainers</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          All trainers have field experience and certified training credentials
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-file-list-line text-[#20B2AA]"></i>
                          </div>
                          <h4 className="font-bold text-[#333333]">Standardized Curriculum</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          Consistent curriculum adapted to local contexts and needs
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-feedback-line text-[#2D8B57]"></i>
                          </div>
                          <h4 className="font-bold text-[#333333]">Continuous Assessment</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          Regular evaluation and feedback throughout the training process
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Assessment Framework */}
                <div className="bg-[#F8F9FA] rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Assessment Framework</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#8B4513]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-brain-line text-2xl text-[#8B4513]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Knowledge</h4>
                      <p className="text-gray-600 text-sm mb-2">Theory & Concepts</p>
                      <div className="text-xs text-gray-500">25% weight</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-hand-heart-line text-2xl text-[#20B2AA]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Skills</h4>
                      <p className="text-gray-600 text-sm mb-2">Practical Application</p>
                      <div className="text-xs text-gray-500">40% weight</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-heart-line text-2xl text-[#2D8B57]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Attitude</h4>
                      <p className="text-gray-600 text-sm mb-2">Sustainability Mindset</p>
                      <div className="text-xs text-gray-500">20% weight</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-lightbulb-line text-2xl text-[#FF6B35]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Innovation</h4>
                      <p className="text-gray-600 text-sm mb-2">Creative Solutions</p>
                      <div className="text-xs text-gray-500">15% weight</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Call to Action */}
        <section className="py-16 bg-[#8B4513] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Join Our Field School Program
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Transform your skills and livelihood through hands-on learning in sustainable aquaculture and conservation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#8B4513] whitespace-nowrap"
                  href="/program"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Program
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#8B4513] whitespace-nowrap"
                  href="/tentang"
                >
                  <i className="ri-information-line mr-2"></i>
                  Learn More About M4CR
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
